import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { normalizeUsername, isUsernameTaken } from '../lib/usernames';
import { deleteAllVoiceNotesForAccount } from '../lib/voiceNoteStorage';
import { fetchMyDataExport } from '../lib/exportUserData';
// ─── tiny toast ──────────────────────────────────────────────────────────────
function Toast({ toasts }) {
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '12px 18px', borderRadius: 'var(--radius-10)', fontSize: 'var(--font-size-md)', fontWeight: 500,
          background: t.type === 'error' ? '#fef2f2' : 'var(--feedback-success-bg)',
          color:      t.type === 'error' ? '#b91c1c'  : '#166534',
          border:     `1px solid ${t.type === 'error' ? '#fca5a5' : '#86efac'}`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          animation: 'slideIn 0.2s ease',
        }}>
          {t.type === 'error' ? '✕ ' : '✓ '}{t.msg}
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };
  return { toasts, success: msg => add(msg, 'success'), error: msg => add(msg, 'error') };
}

// ─── section card ─────────────────────────────────────────────────────────────
function Card({ children, danger }) {
  return (
    <div style={{
      background: 'var(--brand-white)', borderRadius: 'var(--radius-xl)', padding: '28px 32px',
      border: danger ? '1px solid #fca5a5' : '1px solid var(--brand-sand)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      marginBottom: 'var(--space-xl)',
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ icon, title, desc }) {
  return (
    <div style={{ marginBottom: 'var(--space-xxl)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-10)', marginBottom: 'var(--space-xs)' }}>
        <span style={{ fontSize: 'var(--font-size-xl)' }}>{icon}</span>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xl)', fontWeight: 700, color: '#1B110D', margin: 'var(--space-none)' }}>{title}</h2>
      </div>
      {desc && <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-500)', margin: '0 0 0 28px', lineHeight: 1.5 }}>{desc}</p>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 'var(--space-xl)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-base)', fontWeight: 600, color: 'var(--neutral-600)', marginBottom: 'var(--space-6)' }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', margin: '5px 0 0' }}>{hint}</p>}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 'var(--radius-10)',
  border: '1px solid var(--brand-sand)', fontSize: 'var(--font-size-md)', color: '#1B110D',
  background: '#faf9f8', outline: 'none', fontFamily: 'inherit',
  transition: 'border-color 0.15s',
};

function Input({ value, onChange, type = 'text', placeholder, disabled, prefix }) {
  const [focused, setFocused] = useState(false);
  if (prefix) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center',
        border: `1px solid ${focused ? '#d97706' : '#e8e4dc'}`,
        borderRadius: 'var(--radius-10)', background: '#faf9f8', overflow: 'hidden', transition: 'border-color 0.15s',
      }}>
        <span style={{ padding: '10px 12px', fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', borderRight: '1px solid var(--brand-sand)', background: '#f5f4f2', flexShrink: 0 }}>{prefix}</span>
        <input
          value={value} onChange={onChange} type={type} placeholder={placeholder} disabled={disabled}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{ ...inputStyle, border: 'none', background: 'transparent', flex: 1, borderRadius: 'var(--radius-none)' }}
        />
      </div>
    );
  }
  return (
    <input
      value={value} onChange={onChange} type={type} placeholder={placeholder} disabled={disabled}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={{ ...inputStyle, borderColor: focused ? 'var(--feedback-warning)' : 'var(--brand-sand)', display: 'block' }}
    />
  );
}

function SaveButton({ loading, disabled, onClick, label = 'Save changes' }) {
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{
      padding: '10px 24px', background: disabled || loading ? 'var(--brand-sand)' : 'var(--brand-rust)',
      color: disabled || loading ? 'var(--neutral-400)' : 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)',
      fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: disabled || loading ? 'not-allowed' : 'pointer',
      transition: 'background 0.15s',
    }}>
      {loading ? 'Saving…' : label}
    </button>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function SettingsPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Profile fields
  const [fullName, setFullName]     = useState('');
  const [username, setUsername]     = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // ── Email
  const [newEmail, setNewEmail]   = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // ── Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords]     = useState(false);
  const [savingPassword, setSavingPassword]   = useState(false);

  // ── Delete account
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showDeleteModal, setShowDeleteModal]     = useState(false);
  const [deleting, setDeleting]                   = useState(false);

  // ── Export my data (ticket 6b piece 6)
  const [exportingData, setExportingData] = useState(false);

  // ── Nav tab (mobile)
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    async function load() {
      const u = await getCurrentUser();
      if (!u) { navigate('/login'); return; }
      setUser(u);

      const { data: p } = await supabase
        .from('user_profiles')
        .select('display_name, username, avatar_url')
        .eq('user_id', u.id)
        .maybeSingle();

      setProfile(p || {});
      setFullName(p?.display_name || '');
      setUsername(p?.username  || '');
      setLoading(false);
    }
    load();
  }, [navigate]);

  // ── Save profile ──────────────────────────────────────────────────────────
  async function handleSaveProfile() {
    if (!username.trim()) { toast.error('Username cannot be empty'); return; }
    const cleaned = normalizeUsername(username);
    if (cleaned !== username) {
      setUsername(cleaned);
      toast.error('Username can only contain lowercase letters, numbers and underscores');
      return;
    }
    setSavingProfile(true);
    try {
      // Check username uniqueness (skip if unchanged)
      if (cleaned !== profile?.username) {
        const taken = await isUsernameTaken(cleaned, { excludeUserId: user.id });
        if (taken) { toast.error('That username is already taken'); setSavingProfile(false); return; }
      }

      const { error } = await supabase
        .from('user_profiles')
        .update({ display_name: fullName.trim(), username: cleaned })
        .eq('user_id', user.id);

      if (error) throw error;
      setProfile(prev => ({ ...prev, display_name: fullName.trim(), username: cleaned }));
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  }

  // ── Save email ────────────────────────────────────────────────────────────
  async function handleSaveEmail() {
    if (!newEmail.trim() || !newEmail.includes('@')) { toast.error('Please enter a valid email'); return; }
    if (newEmail === user.email) { toast.error('That is already your current email'); return; }
    setSavingEmail(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (error) throw error;
      toast.success('Confirmation sent, check both inboxes to confirm the change');
      setNewEmail('');
    } catch (err) {
      toast.error(err.message || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  }

  // ── Save password ─────────────────────────────────────────────────────────
  async function handleSavePassword() {
    if (!newPassword) { toast.error('Please enter a new password'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Password updated');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  }

  // ── Export my data ────────────────────────────────────────────────────────
  // Ticket 6b piece 6. "Export my data must include the raw audio files,
  // not only text" -- so this downloads the JSON (every entry's prompt,
  // text, transcript) AND, separately, the actual audio file for every
  // entry that has a stored voice note. No zip -- see
  // export-user-data/index.ts's own header for why. Downloads are
  // triggered one at a time with a short gap between them; firing a burst
  // of anchor.click() calls in the same tick makes some browsers silently
  // drop everything after the first.
  async function handleExportData() {
    setExportingData(true);
    try {
      const result = await fetchMyDataExport();

      const jsonBlob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const jsonUrl = URL.createObjectURL(jsonBlob);
      const jsonLink = document.createElement('a');
      jsonLink.href = jsonUrl;
      jsonLink.download = `reanlo-journal-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(jsonLink);
      jsonLink.click();
      jsonLink.remove();
      URL.revokeObjectURL(jsonUrl);

      const entries = result.entries || [];
      const audioEntries = entries.filter(e => e.audio_download_url);
      for (const entry of audioEntries) {
        const audioLink = document.createElement('a');
        audioLink.href = entry.audio_download_url;
        document.body.appendChild(audioLink);
        audioLink.click();
        audioLink.remove();
        await new Promise(res => setTimeout(res, 400));
      }

      const failedAudio = entries.filter(e => e.has_voice_note && !e.audio_download_url);
      if (failedAudio.length > 0) {
        toast.error(`Downloaded your entries, but ${failedAudio.length} voice note${failedAudio.length > 1 ? 's' : ''} didn't come through, try again in a moment.`);
      } else {
        const audioNote = audioEntries.length ? ` and ${audioEntries.length} voice note${audioEntries.length > 1 ? 's' : ''}` : '';
        toast.success(`Exported ${result.entry_count} journal ${result.entry_count === 1 ? 'entry' : 'entries'}${audioNote}.`);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to export your data');
    } finally {
      setExportingData(false);
    }
  }

  // ── Delete account ────────────────────────────────────────────────────────
  async function handleDeleteAccount() {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      // Voice note objects first (ticket 6b, spec 9.10 event 2), then the
      // profile row below. The edge function call needs the caller's own
      // auth token, which is still good at this point -- signOut is the
      // very last thing this function does, only once everything else has
      // succeeded.
      //
      // Best-effort on purpose, unlike the profile delete right after it:
      // a failure here does NOT stop account deletion. The three-year GCS
      // lifecycle rule is the documented backstop for exactly this case
      // (9.10: "a backstop, not the promise"), and a reader who asked to
      // be deleted must not end up stuck, unable to delete their account,
      // because a storage cleanup call had a bad day. Logged, not
      // swallowed silently, so a real recurring failure is visible.
      try {
        const { failed } = await deleteAllVoiceNotesForAccount();
        if (failed.length > 0) {
          console.error(`Account deletion: ${failed.length} voice note object(s) could not be removed for user ${user.id}`, failed);
        }
      } catch (err) {
        console.error('Account deletion: voice note cleanup failed:', err.message);
      }

      // Delete profile data next.
      //
      // This was unchecked, and it is the worst place in the app for that. A
      // delete refused by RLS changed nothing, and the next two lines signed
      // the person out and sent them to the homepage — so they were told their
      // account was gone, could no longer see that it was not, and had no
      // reason to check. Someone asking to be deleted and quietly not being
      // deleted is the one outcome this screen must never produce.
      //
      // Stop before signing out, so they can try again or tell us.
      const { error } = await supabase.from('user_profiles').delete().eq('user_id', user.id);
      if (error) {
        toast.error(`Your account was NOT deleted and nothing was changed: ${error.message}`);
        setDeleting(false);
        return;
      }
      // Sign out — server-side delete requires admin key; this effectively locks them out
      // For a real hard delete, call a Supabase Edge Function that uses the service role
      await supabase.auth.signOut();
      navigate('/');
    } catch (err) {
      toast.error(err.message || 'Failed to delete account');
      setDeleting(false);
    }
  }

  const tabs = [
    { key: 'profile',  label: '👤 Profile'  },
    { key: 'email',    label: '✉️ Email'     },
    { key: 'password', label: '🔒 Password'  },
    { key: 'danger',   label: '⚠️ Account'   },
  ];

  const profileDirty = fullName !== (profile?.display_name || '') || username !== (profile?.username || '');

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FCF9F8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--feedback-warning-border)', borderTopColor: 'var(--feedback-warning)', borderRadius: 'var(--radius-circle)', animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FCF9F8', fontFamily: 'var(--font-body)', color: '#1B110D', paddingBottom: 80 }}>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        input:focus { outline: none; }
        .settings-layout { display: flex; max-width: 860px; margin: 0 auto; padding: 40px 32px; gap: 32px; align-items: flex-start; }
        .settings-nav { width: 192px; flex-shrink: 0; position: sticky; top: 80px; }
        .settings-main { flex: 1; min-width: 0; }
        .settings-mobile-tabs { display: none; }
        @media (max-width: 680px) {
          .settings-layout { flex-direction: column; padding: 24px 16px; gap: 0; }
          .settings-nav { display: none; }
          .settings-mobile-tabs { display: flex; overflow-x: auto; gap: 8px; padding: 16px 16px 0; scrollbar-width: none; -ms-overflow-style: none; }
          .settings-mobile-tabs::-webkit-scrollbar { display: none; }
        }
      `}</style>


      {/* ── Mobile tabs ── */}
      <div className="settings-mobile-tabs">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 16px', borderRadius: 'var(--radius-xxl)', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
            fontSize: 'var(--font-size-base)', fontWeight: 600,
            background: activeTab === t.key ? (t.key === 'danger' ? 'var(--brand-rust)' : '#1B110D') : 'var(--brand-white)',
            color:      activeTab === t.key ? 'var(--brand-white)' : 'var(--neutral-500)',
            border:     activeTab === t.key ? 'none' : '1px solid var(--brand-sand)',
            flexShrink: 0,
          }}>{t.label}</button>
        ))}
      </div>

      <div className="settings-layout">

        {/* ── Desktop sidebar nav ── */}
        <nav className="settings-nav">
          <p style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--neutral-400)', margin: '0 0 12px' }}>Account</p>
          {tabs.map(t => (
            <a key={t.key} href={`#${t.key}`} onClick={e => { e.preventDefault(); document.getElementById(t.key)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-10)',
              padding: '9px 12px', borderRadius: 'var(--radius-10)', marginBottom: 'var(--space-xxs)',
              textDecoration: 'none', fontSize: 'var(--font-size-md)', fontWeight: 500,
              color: t.key === 'danger' ? 'var(--brand-rust)' : 'var(--neutral-600)',
              background: 'transparent',
              transition: 'background 0.12s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f4f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >{t.label}</a>
          ))}
        </nav>

        {/* ── Main content ── */}
        <div className="settings-main">

          {/* ── PROFILE ── */}
          <div id="profile">
            <Card>
              <SectionTitle icon="👤" title="Profile" desc="Your display name and public username" />

              <Field label="Full name" hint="Shown on your profile and to other readers">
                <Input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your name" />
              </Field>

              <Field label="Username" hint="Letters, numbers and underscores only · reanloforreaders.com/u/username">
                <Input value={username} onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="username" prefix="@" />
              </Field>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <SaveButton loading={savingProfile} disabled={!profileDirty} onClick={handleSaveProfile} />
              </div>
            </Card>
          </div>

          {/* ── EMAIL ── */}
          <div id="email">
            <Card>
              <SectionTitle icon="✉️" title="Email address" desc="Update the email address linked to your account" />

              <Field label="Current email">
                <Input value={user?.email || ''} disabled />
              </Field>

              <Field label="New email address" hint="We'll send a confirmation link to both your old and new address">
                <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} type="email" placeholder="new@email.com" />
              </Field>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <SaveButton loading={savingEmail} disabled={!newEmail.trim()} onClick={handleSaveEmail} label="Update email" />
              </div>
            </Card>
          </div>

          {/* ── PASSWORD ── */}
          <div id="password">
            <Card>
              <SectionTitle icon="🔒" title="Password" desc="Choose a strong password of at least 8 characters" />

              <Field label="New password">
                <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} type={showPasswords ? 'text' : 'password'} placeholder="••••••••" />
              </Field>

              <Field label="Confirm new password">
                <Input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type={showPasswords ? 'text' : 'password'} placeholder="••••••••" />
              </Field>

              {/* Password match indicator */}
              {newPassword && confirmPassword && (
                <div style={{
                  fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-lg)',
                  color: newPassword === confirmPassword ? '#166534' : '#b91c1c',
                }}>
                  {newPassword === confirmPassword ? '✓ Passwords match' : '✕ Passwords do not match'}
                </div>
              )}

              {/* Strength bar */}
              {newPassword && (
                <div style={{ marginBottom: 'var(--space-xl)' }}>
                  <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
                    {[1,2,3,4].map(i => {
                      const strength = newPassword.length >= 12 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[^a-zA-Z0-9]/.test(newPassword) ? 4
                        : newPassword.length >= 10 && /[A-Z0-9]/.test(newPassword) ? 3
                        : newPassword.length >= 8 ? 2 : 1;
                      return (
                        <div key={i} style={{
                          height: 4, flex: 1, borderRadius: 2,
                          background: i <= strength ? (strength >= 3 ? '#16a34a' : strength === 2 ? 'var(--feedback-warning)' : '#b91c1c') : 'var(--brand-sand)',
                          transition: 'background 0.2s',
                        }} />
                      );
                    })}
                  </div>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)' }}>
                    {newPassword.length < 8 ? 'Too short' : newPassword.length < 10 ? 'Weak' : newPassword.length >= 12 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) ? 'Strong' : 'Good'}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button onClick={() => setShowPasswords(!showPasswords)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--neutral-500)', fontSize: 'var(--font-size-base)', padding: 'var(--space-none)' }}>
                  {showPasswords ? '🙈 Hide' : '👁 Show'} password
                </button>
                <SaveButton loading={savingPassword} disabled={!newPassword || !confirmPassword} onClick={handleSavePassword} label="Update password" />
              </div>
            </Card>
          </div>

          {/* ── EXPORT MY DATA ── */}
          <div id="export">
            <Card>
              <SectionTitle icon="📦" title="Export my data" desc="Download your journal entries as text, plus every voice note you've recorded as its own audio file." />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={handleExportData} disabled={exportingData} style={{
                  padding: '10px 24px', background: exportingData ? 'var(--brand-sand)' : 'var(--brand-rust)',
                  color: exportingData ? 'var(--neutral-400)' : 'var(--brand-white)', border: 'none', borderRadius: 'var(--radius-10)',
                  fontSize: 'var(--font-size-md)', fontWeight: 700, cursor: exportingData ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}>
                  {exportingData ? 'Exporting…' : 'Export my data'}
                </button>
              </div>
            </Card>
          </div>

          {/* ── DANGER ZONE ── */}
        
          {/* ── Legal links ── */}
          <div style={{ display: 'flex', gap: 'var(--space-lg)', padding: '8px 0', flexWrap: 'wrap' }}>
            <a href="/privacy" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textDecoration: 'none' }}>Privacy Policy</a>
            <span style={{ color: 'var(--brand-sand)', fontSize: 'var(--font-size-sm)' }}>·</span>
            <a href="/terms" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textDecoration: 'none' }}>Terms of Service</a>
            <span style={{ color: 'var(--brand-sand)', fontSize: 'var(--font-size-sm)' }}>·</span>
            <button onClick={async () => { await supabase.auth.signOut(); navigate('/'); }} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', background: 'none', border: 'none', cursor: 'pointer', padding: 'var(--space-none)' }}>Sign out</button>
          </div>
        </div>
      </div>

      {/* ── Delete confirmation modal ── */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-xl)' }} onClick={() => setShowDeleteModal(false)}>
          <div style={{ background: 'var(--brand-white)', borderRadius: 'var(--radius-xxl)', maxWidth: 420, width: '100%', padding: 'var(--space-h3)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-xxl)' }}>
              <div style={{ fontSize: 'var(--font-size-hero)', marginBottom: 'var(--space-md)' }}>🗑️</div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--font-size-xxl)', fontWeight: 700, color: '#1B110D', margin: '0 0 8px' }}>Delete your account?</h2>
              <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-500)', margin: 'var(--space-none)', lineHeight: 1.6 }}>
                This will permanently delete your profile, library, and all activity. There's no going back.
              </p>
            </div>

            <Field label={<span>Type <strong style={{ color: '#b91c1c' }}>DELETE</strong> to confirm</span>}>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
              />
            </Field>

            <div style={{ display: 'flex', gap: 'var(--space-10)' }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} style={{ flex: 1, padding: 'var(--space-md)', background: '#f5f4f2', color: 'var(--neutral-600)', border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                style={{
                  flex: 1, padding: 'var(--space-md)',
                  background: deleteConfirmText === 'DELETE' && !deleting ? '#b91c1c' : 'var(--brand-sand)',
                  color: deleteConfirmText === 'DELETE' && !deleting ? 'var(--brand-white)' : 'var(--neutral-400)',
                  border: 'none', borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-md)', fontWeight: 700,
                  cursor: deleteConfirmText === 'DELETE' && !deleting ? 'pointer' : 'not-allowed',
                }}
              >
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toasts={toast.toasts} />
    </div>
  );
}